import { z } from "zod";
import { GOAL_SCOPES, GOAL_STATUSES } from "@/features/goals/goal-vocabulary";

/// URL search-params schema for `/$slug/goals`. The `view` param drives the
/// page's tab nav: ONE sidebar entry, role-aware tabs, each tab a different
/// lens (never a subset filter, per the M2 scope-tabs convention).
///
///   mine      , goals I'm assigned to (default, everyone)
///   created   , goals I created (everyone)
///   all       , everything I can see via chain visibility (everyone)
///   workspace , every goal in the workspace; bypasses chain visibility
///                (admins via GOAL_VIEW_WORKSPACE)
///
/// `view` (not `scope`) names the URL param because `scope` is already a goal
/// domain noun (individual / department / workspace) used as a filter below.
export const GOAL_VIEWS = ["mine", "created", "all", "workspace"] as const;
export type GoalView = (typeof GOAL_VIEWS)[number];

/// `display` is orthogonal to `view`: it picks the layout the current lens is
/// rendered in. `list` is the flat table; `tree` is the alignment forest (PRD
/// §6.2, S2.4) built client-side from the same visible rows. The two URL
/// dimensions compose, e.g. `?view=workspace&display=tree`.
export const GOAL_DISPLAYS = ["list", "tree"] as const;
export type GoalDisplay = (typeof GOAL_DISPLAYS)[number];

export const goalsSearchSchema = z
  .object({
    view: z.enum(GOAL_VIEWS).catch("all").default("all"),
    display: z.enum(GOAL_DISPLAYS).catch("tree").default("tree"),
    status: z.enum(GOAL_STATUSES).optional(),
    scope: z.enum(GOAL_SCOPES).optional(),
    departmentId: z.string().optional(),
  })
  .strict();
export type GoalsSearch = z.infer<typeof goalsSearchSchema>;
